import { apiClient } from '@/lib/api-client';

/** Mirrors CATALOG_ENTITY_TYPES on the API (catalog-approval.helper.ts). */
export type CatalogType =
  | 'ACTIVITY'
  | 'TRANSFER'
  | 'HOTEL'
  | 'MEAL_SERVICE'
  | 'VISA'
  | 'INSURANCE'
  | 'FIXED_DEPARTURE'
  | 'CRUISE';

/**
 * Mirrors the CatalogApprovalAction enum. The last four were added 2026-07-29
 * so History covers writes that never hit the approval gate - drafts, direct
 * edits before first approval, the media-publish bypass, and deletes.
 */
export type CatalogApprovalAction =
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'STATUS_CHANGED'
  | 'CREATED'
  | 'UPDATED'
  | 'MEDIA_PUBLISHED'
  | 'DELETED';

export interface PendingApprovalItem {
  entityType: CatalogType;
  id: string;
  name: string;
  status: string;
  city: string | null;
  country: string | null;
  hasPendingChange: boolean;
  submittedById: string | null;
  /** Who submitted it for approval; null when unknown or the user was deleted. */
  submittedByName?: string | null;
  submittedAt: string | null;
  /** Present on APPROVED / REJECTED rows only - when the decision was taken. */
  decidedAt?: string | null;
  /** Present on APPROVED / REJECTED rows only - who took it. */
  decidedByName?: string | null;
  updatedAt: string;
}

export interface PendingApprovalList {
  data: PendingApprovalItem[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export interface CatalogApprovalLog {
  id: string;
  action: string;
  actorName: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  note: string | null;
  createdAt: string;
}

export interface CatalogApprovalsQuery {
  type?: CatalogType | 'ALL';
  /** Decision slice. Defaults to PENDING server-side when omitted. */
  status?: 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';
  /** Narrow to one submitter. */
  requestedById?: string;
  /** Narrow to one decision-maker; empties the Pending slice by design. */
  reviewedById?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const listCatalogApprovals = async (
  query: CatalogApprovalsQuery,
): Promise<PendingApprovalList> => {
  const { data } = await apiClient.get<PendingApprovalList>('/admin/catalog-approvals', {
    params: query,
  });
  return data;
};

export const getCatalogApprovalHistory = async (
  type: CatalogType,
  id: string,
): Promise<CatalogApprovalLog[]> => {
  const { data } = await apiClient.get<CatalogApprovalLog[]>(
    `/admin/catalog-approvals/${type}/${id}/history`,
  );
  return data;
};

/** One row of the global History tab. */
export interface CatalogHistoryEntry {
  id: string;
  entityType: CatalogType;
  entityId: string;
  /** Current product name; null once the row is hard-deleted. */
  entityName: string | null;
  action: CatalogApprovalAction;
  actorId: string | null;
  actorName: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  note: string | null;
  createdAt: string;
}

export interface CatalogHistoryList {
  data: CatalogHistoryEntry[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export interface CatalogHistoryQuery {
  type?: CatalogType | 'ALL';
  action?: CatalogApprovalAction | 'ALL';
  actorId?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Every recorded change across Inventory & Supply, newest first - not just the
 * ones awaiting a decision. Pass `actorId` to see one person's trail (e.g. the
 * website manager's).
 */
export const listCatalogHistory = async (
  query: CatalogHistoryQuery,
): Promise<CatalogHistoryList> => {
  const { data } = await apiClient.get<CatalogHistoryList>('/admin/catalog-approvals/history', {
    params: query,
  });
  return data;
};

export const approveCatalogItem = async (
  type: CatalogType,
  id: string,
  body: { status?: string; note?: string },
): Promise<unknown> => {
  const { data } = await apiClient.post(`/admin/catalog-approvals/${type}/${id}/approve`, body);
  return data;
};

/** `note` is the rejection reason - required, and enforced on the API too. */
export const rejectCatalogItem = async (
  type: CatalogType,
  id: string,
  body: { note: string },
): Promise<unknown> => {
  const { data } = await apiClient.post(`/admin/catalog-approvals/${type}/${id}/reject`, body);
  return data;
};

/** One selected queue row. The id alone is not addressable - the queue mixes
 *  six catalog tables, so the type travels with it. */
export interface CatalogApprovalRef {
  type: CatalogType;
  id: string;
}

/** Partial success: a bad id lands in `failed`, the rest still go through. */
export interface BulkDecisionResult {
  updated: number;
  failed: { id: string; error: string }[];
}

export const bulkApproveCatalogItems = async (
  items: CatalogApprovalRef[],
): Promise<BulkDecisionResult> => {
  const { data } = await apiClient.post<BulkDecisionResult>(
    '/admin/catalog-approvals/bulk-approve',
    { items },
  );
  return data;
};

/** `note` is the rejection reason - one reason covers the whole selection, and
 *  it is recorded against every item's approval trail. */
export const bulkRejectCatalogItems = async (
  items: CatalogApprovalRef[],
  note: string,
): Promise<BulkDecisionResult> => {
  const { data } = await apiClient.post<BulkDecisionResult>(
    '/admin/catalog-approvals/bulk-reject',
    {
      items,
      note,
    },
  );
  return data;
};
